/**
 * notifications/Notifications.tsx — /notifications (NT-F09). The full feed for the six PERSONAL
 * kinds the backend writes: 3 (comment/issue on your tool), 4 (your submission's status), 5 (a
 * submission is waiting for your review), 6 (a reply to your post), 7 (the outcome on your content, OR
 * on a report you raised) and 8 (your tool's first Top 5). Provisional
 * engineering UI — there is no Will UX flow for notifications yet, same as Community Hub.
 *
 * LEGACY, AND SHELVED. The Kombi header's bell IS the feed now (kombi/Header.tsx), and this page is
 * retained only for links that predate it. Nothing may link here any more — the generic row's href used
 * to, which meant an unrendered kind dropped a colleague out of the new interface. Every destination
 * `rowFor` returns is a Kombi route.
 *
 * Never names a third party (NT-F06/F07): each row's line is resolved from `kind` plus the governed
 * enum in the payload, never from an actor's name or body text — the payload carries neither. Kind
 * 7's `reason` is the ONE piece of prose here and it is the documented exception
 * (db/notification.ts's header): the decider's own words about the recipient's OWN content, written
 * to be told to them, and the only place the platform ever delivers them (nothing else on any screen
 * shows `removal_reason`). Delivering it is the whole point of FB-F09/FB-F16/FB-F17/PC-F36.
 *
 * KIND 7 HAS TWO WRITERS AND THEY MEAN OPPOSITE THINGS — see `rowFor`. `notifyOutcomeOnYourContent`
 * writes to the person whose OWN content was judged and carries the reason; `notifyOutcomeOnYourReport`
 * writes to a person who REPORTED somebody else's content and carries no reason at all. Telling the
 * second group what the first group is told is the accusation of a moderation decision they never had.
 */
import { Link, useLoaderData, useRevalidator } from 'react-router';
import { apiGet } from '../community/api';
import { ProvisionalBanner, SignedOut, shortWhen } from '../community/shared';
import { clearAllNotifications, markAllNotificationsRead } from './api';
import type { NotificationEntry, NotificationFeed } from './api';
// The non-`/:id` destinations, shared with the bell's `describe` rather than kept as a second copy.
// `rowFor` stays a separate implementation — the WORDS differ deliberately — but the PATHS must not,
// and two of them were quietly still pointing into the legacy frame. See that module's own note.
import { NOTIFICATION_DESTINATIONS } from './describe';

export type NotificationsLoaderData =
  { state: 'signedOut' } | { state: 'ok'; notifications: NotificationEntry[]; unreadCount: number };

export async function notificationsLoader(): Promise<NotificationsLoaderData> {
  const res = await apiGet<NotificationFeed>('/api/notifications');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') throw new Error(`GET /api/notifications failed (${res.status})`);
  return { state: 'ok', notifications: res.body.notifications, unreadCount: res.body.unreadCount };
}

/**
 * Where "how to respond" goes (NT-F17). Community Hub's "Ask for help" section (community/Community.tsx
 * `AskForHelp`, `<h2 id="help-heading">`) is the ONLY raise-a-request entry point the platform has, and
 * an ask-for-help request reaches the SWAT team and the platform owner through the support queue — so
 * it is a real destination for "I think this decision is wrong", not a new feature and not a dead link.
 *
 * The fragment is deliberate but does NOT scroll today: this app mounts no `ScrollRestoration` and
 * react-router does not act on a hash by itself, so a client-side navigation lands at the top of
 * /community with the section further down the page. It is written as an anchor anyway because it is
 * the honest target, it is correct on a full page load, and it starts working the moment hash handling
 * is added. Nothing here promises a jump.
 */
const RESPOND_HREF = '/community#help-heading';

/**
 * The one place a feed row becomes text — and it RETURNS A ROW FOR EVERY KIND, deliberately.
 *
 * The unread count is computed in SQL over the same rows (db/notification.ts `unreadCount`) and
 * cannot know which of them this function is willing to draw, so a `null` return here is a bell
 * counting notifications over an empty list. That was live: the backend writes kinds 4, 7 and 8 and
 * this rendered only 3 and 6, so a moderation decision, a comment removal and a support-status change
 * all notified the recipient invisibly. The return type is non-nullable now, so the same gap cannot
 * reopen quietly — a kind nobody has wired yet still gets an honest generic line.
 *
 * KIND 1 IS ABSENT ON PURPOSE AND IS NOT A GAP: it is announced to the community channel, so its row
 * has `recipient_user_id` NULL (migration 0010) and this feed reads `recipient_user_id = you`. No
 * personal feed can ever return it.
 *
 * Every href is a same-site path built from an id in the payload, with a fallback surface for the
 * (unwritable today) case of a payload missing its id: `/me` for your own tool, `/community` for
 * forum content and support requests. A row that cannot be linked precisely still says what happened.
 * The one row that is ALWAYS imprecise is the reporter's (`subjectKind: 'report'`) — see there.
 *
 * EXPORTED ONLY SO IT CAN BE TESTED (test/notificationRows.test.ts): it is a pure payload → words/href
 * function and it is where every NT-F06/F07 judgement on this page is made, so it is the one part of
 * this file worth pinning down without a DOM. Nothing else imports it.
 */
export function rowFor(n: NotificationEntry): {
  text: string;
  href: string;
  reason?: string;
  /** NT-F17's second half: set only where a reason is delivered, i.e. a decision on your OWN content. */
  respond?: { href: string; label: string };
} {
  // One defensively-defaulted view of the payload for all five kinds: it is JSONB written only by
  // notify(), so it is always a flat object of ids and governed enums — but a null/scalar row would
  // throw on property access and take the whole list down with it, which is the same defect as
  // rendering nothing. Kind 7's `context` is spread FLAT onto the payload by the backend, which is
  // why threadId/toolId sit alongside subjectKind rather than nested — there is no nested `context`
  // key to read, and reading one is how every kind-7 href came out as a bare /community once before.
  const p = (typeof n.payload === 'object' && n.payload !== null ? n.payload : {}) as {
    toolId?: string;
    issueId?: string;
    threadId?: string;
    postId?: string;
    subjectKind?: string;
    status?: string;
    reason?: string;
    // Kind 7's reporter limb only (`subjectKind: 'report'`): what was reported, as a governed enum
    // plus its row id. `targetId` is deliberately unused in any href — see the 'report' arm.
    targetKind?: string;
    targetId?: string;
    // Kind 5 only: which needs-attention limb fired, and the submitted tool's own name.
    name?: string;
  };
  const toolHref = typeof p.toolId === 'string' ? `/tools/${encodeURIComponent(p.toolId)}` : '/me';
  const threadHref =
    typeof p.threadId === 'string'
      ? `/community/thread/${encodeURIComponent(p.threadId)}` +
        // ?highlight=<postId> lets the thread page scroll to and flash the exact post being pointed
        // at (a one-time cue THERE, not a name or body here).
        (typeof p.postId === 'string' ? `?highlight=${encodeURIComponent(p.postId)}` : '')
      : '/community';
  const status = typeof p.status === 'string' ? p.status : null;

  if (n.kind === 3) {
    return {
      text: 'issueId' in p ? 'New issue report on your tool' : 'New comment on your tool',
      href: toolHref,
    };
  }
  if (n.kind === 4) {
    // `{toolId, reviewId, status}`. `status` is the governed PublicationStatus — the catalogue's own
    // word for what the tool moved TO (never the reviewer's lower-case 'approved'/'rejected'
    // decision, which the type deliberately rejects at the call site), so it is safe verbatim.
    return {
      text:
        status === null
          ? 'Your tool submission changed status'
          : status === 'Approved' || status === 'Rejected' || status === 'Withdrawn'
            ? `Your tool submission was ${status.toLowerCase()}`
            : `Your tool submission is now ${status}`,
      href: toolHref,
    };
  }
  if (n.kind === 5) {
    // WIRED 2026-09-02, UNRENDERED UNTIL NOW — and the defect this file's own header warns about,
    // repeated. `notifySubmissionAwaitingReview` fans kind 5 out to every Reviewer; this function and
    // the bell's `describe` both fell through to the generic arm, so a reviewer got "You have a new
    // notification" pointing at this page, which the Kombi header has shelved. See `describe` for why
    // this switches on `reason` rather than assuming the submission limb.
    //
    // CAREFUL: `payload.reason` MEANS SOMETHING ELSE HERE THAN IT DOES FOR KIND 7. On kind 5 it is a
    // governed enum naming which needs-attention limb fired; on kind 7 it is the decider's prose, which
    // this function returns as `reason` for the page to print. This arm returns before that happens, and
    // it must keep doing so — a kind 5 falling through would print `submission_awaiting_review` to a
    // reviewer as though a moderator had written it about them.
    const name = typeof p.name === 'string' && p.name !== '' ? p.name : null;
    if (p.reason === 'submission_awaiting_review') {
      return {
        // The tool's own name, sent by the writer so the row can say WHICH submission without a second
        // read. A submitted automation is not a third party, so NT-F06/F07 does not bar it.
        text:
          name === null
            ? 'A submission is waiting for your review'
            : `${name} is waiting for your review`,
        href: NOTIFICATION_DESTINATIONS.reviewQueue,
      };
    }
    return { text: 'Something needs your attention', href: NOTIFICATION_DESTINATIONS.admin };
  }
  if (n.kind === 6) {
    return { text: 'New reply to your post', href: threadHref };
  }
  if (n.kind === 7) {
    // THE REPORTER'S LIMB FIRST (CH-F14 / FB-F17), because it is the one row on this page written to
    // somebody who was NOT judged. `notifyOutcomeOnYourReport` sends `{subjectKind: 'report',
    // subjectId: flagId, status, targetKind, targetId}` — no reason, no author, no body, by design
    // (NT-F06/F07: the moderator's words are about another person's content and are the author's to
    // read). It fell through to the generic arm until now, so the person who RAISED a report was told
    // "A decision was made about your content", i.e. accused of having had their own post moderated —
    // the exact opposite of the fact, and the fall-through's bare /community with it.
    if (p.subjectKind === 'report') {
      // `targetKind` is app.content_flag's governed 'comment' | 'forum_post', so unlike the author's
      // limb below this noun needs no guessing from context ids — there are none in this payload.
      const reported =
        p.targetKind === 'forum_post'
          ? 'post'
          : p.targetKind === 'comment'
            ? 'tool comment'
            : 'content';
      return {
        text:
          // Both outcomes notify (see the writer): a reporter told on 'remove' and not on 'dismiss'
          // learns the outcome from the silence, so both say plainly what happened to the content.
          status === 'dismiss'
            ? `The ${reported} you reported was reviewed and kept`
            : status === 'remove'
              ? `The ${reported} you reported was removed`
              : `A decision was made on the ${reported} you reported`,
        // AS PRECISE AS THE PAYLOAD ALLOWS, WHICH IS THE KIND AND NOT THE ID. `targetId` is a post id
        // or a tool-comment id, and neither is addressable: the only thread route is
        // /community/thread/:threadId (a post id there would 404) and a tool comment is only reachable
        // via /tools/:toolId, which this payload deliberately does not carry. So the row lands on the
        // right SURFACE for what was reported and says which it was in its own text. Linking to the
        // item itself needs the payload widened (a `threadId` for a forum post, a `toolId` for a tool
        // comment — both already known to the writer in db/moderation.ts) and that is a server change
        // weighed against NT-F06, not a guess to make here from an id that addresses nothing.
        // `NOTIFICATION_DESTINATIONS.toolCatalogue`, not the legacy `/tools` this used to name: the
        // redesigned catalogue has category landing routes and `/tools` is the old frame's page.
        href: p.targetKind === 'comment' ? NOTIFICATION_DESTINATIONS.toolCatalogue : '/community',
      };
    }
    // The author's own limb: `{subjectKind, subjectId, status, reason}` + the decider's `context` ids.
    // A flag can sit on a forum post OR a tool comment and `subjectKind` does not distinguish them,
    // so here the noun comes from which context id arrived with the row.
    const flagged = typeof p.threadId === 'string' ? 'post' : 'comment';
    const text =
      p.subjectKind === 'request'
        ? status === null
          ? 'Your support request was updated'
          : `Your support request is now ${status}`
        : p.subjectKind === 'comment'
          ? 'Your comment on a tool was removed'
          : p.subjectKind === 'flag'
            ? status === 'dismiss'
              ? `Your ${flagged} was reported, reviewed and kept`
              : status === 'remove'
                ? `Your ${flagged} was removed after a report`
                : `A moderator decided the report on your ${flagged}`
            : 'A decision was made about your content';
    return {
      text,
      // A support request has no thread and often no tool (an ask-for-help request carries neither),
      // so it lands on /community, where "Your support requests" lists it with its status history.
      href:
        typeof p.threadId === 'string'
          ? threadHref
          : typeof p.toolId === 'string'
            ? toolHref
            : '/community',
      ...(typeof p.reason === 'string' && p.reason !== '' ? { reason: p.reason } : {}),
      // NT-F17 asks a decision message for the reason AND how to respond, and the reason limb arriving
      // is what left this half visibly missing. It rides on the AUTHOR's limb only: this is where the
      // recipient's own content or own request was judged, so this is where "I think that is wrong" is
      // theirs to say. The reporter's row above gets none — a third party has nothing to appeal, and
      // pointing them at the support queue over another person's content would invite exactly the
      // second bite at the moderation of somebody else's post that NT-F06/F07 keeps them out of.
      respond: { href: RESPOND_HREF, label: 'Disagree, or need to ask about this? Ask for help' },
    };
  }
  if (n.kind === 8) {
    // `{toolId, weekStart}`. Fires at most once per tool, ever, on FIRST entry (RT-F21) — hence
    // "reached", which stays true after the tool drops out again. The Top 5 itself is on the home
    // page, so that is where a row with no tool id points.
    return {
      text: 'Your tool reached the weekly Top 5',
      href: typeof p.toolId === 'string' ? toolHref : '/',
    };
  }
  // Kind 2 (adoption_milestone) is in the closed NT-F02 list but unbuilt, and likes/follow/badge are
  // permanently banned (NT §8), so nothing writes anything else today. If something ever does, the row
  // says so instead of vanishing under an unread count. Kind 5 sat here on that same reasoning while it
  // was already being written — so check db/notification.ts's WIRED KINDS list before believing the
  // arms above are complete. The href no longer points at this page: it was a link to the screen you
  // are already on, and the Kombi bell's copy of it was a door back into the legacy interface.
  return { text: 'You have a new notification', href: NOTIFICATION_DESTINATIONS.unknownKind };
}

export function Notifications() {
  const data = useLoaderData() as NotificationsLoaderData;
  const revalidator = useRevalidator();

  if (data.state === 'signedOut') {
    return <SignedOut title="Notifications" returnTo="/notifications" />;
  }

  async function markAllRead() {
    // Through ./api's wrapper, not a second copy of the path. This used to call
    // `apiPost('/api/notifications/read-all')` inline while importing `clearAllNotifications` from the
    // same module two lines below — so the route string lived in two places and only one of them was
    // covered by a test. If the route moved, this page would 404 silently and still revalidate,
    // redrawing the unread rows with no error shown.
    await markAllNotificationsRead();
    revalidator.revalidate();
  }

  async function clearAll() {
    await clearAllNotifications();
    revalidator.revalidate();
  }

  return (
    <section aria-labelledby="notif-heading" className="tb-community">
      {/* This page carries its own three rules rather than COMMUNITY_STYLES: the reason line needs
          pre-wrap (a moderator's reason arrives with the line breaks they typed), and the respond line
          under it is a quieter secondary action, not a second row title. */}
      <style>
        {'.tb-notif-page__list { list-style: none; margin: 0; padding: 0; }' +
          ' .tb-notif-page__reason { margin: 0.15rem 0 0; white-space: pre-wrap;' +
          ' overflow-wrap: anywhere; max-width: 70ch; font-size: 0.9rem; }' +
          ' .tb-notif-page__respond { margin: 0.15rem 0 0; font-size: 0.85rem; }'}
      </style>
      <h1 id="notif-heading">Notifications</h1>
      <ProvisionalBanner />

      <p className="tb-muted" style={{ maxWidth: '60ch' }}>
        Comments and issue reports on tools you built, replies to posts you wrote in Community Hub,
        decisions on your own content and support requests, the outcome of anything you reported,
        and what happened to a tool you submitted.
      </p>

      {data.notifications.length > 0 ? (
        <div style={{ margin: '0.5rem 0', display: 'flex', gap: '0.5rem' }}>
          {data.unreadCount > 0 ? (
            <button
              className="tb-action"
              type="button"
              onClick={() => void markAllRead()}
              disabled={revalidator.state === 'loading'}
            >
              Mark all as read
            </button>
          ) : null}
          <button
            className="tb-action"
            type="button"
            onClick={() => void clearAll()}
            disabled={revalidator.state === 'loading'}
          >
            Clear all
          </button>
        </div>
      ) : null}

      {data.notifications.length === 0 ? (
        <p>You have no notifications yet.</p>
      ) : (
        <ul className="tb-list tb-notif-page__list">
          {data.notifications.map((n) => {
            // No `if (!row) return null` here, and rowFor's type no longer allows one: a row the
            // list drops is still counted unread, which is the bell showing a count over nothing.
            const row = rowFor(n);
            return (
              <li key={n.notificationId}>
                <div className="tb-list-title">
                  <Link to={row.href}>{row.text}</Link>
                </div>
                {/* The decider's mandatory reason (kind 7 only). Labelled, because unlabelled prose
                    in a feed row reads as the recipient's own words rather than the decision's. */}
                {row.reason === undefined ? null : (
                  <p className="tb-notif-page__reason">
                    <span className="tb-muted">Reason: </span>
                    {row.reason}
                  </p>
                )}
                {/* NT-F17's "how to respond", on the same rows that carry the reason. A link, not a
                    button: it goes to the ask-for-help form that already exists rather than posting
                    anything from here, so there is no half-built appeal workflow behind it. */}
                {row.respond === undefined ? null : (
                  <p className="tb-notif-page__respond">
                    <Link to={row.respond.href}>{row.respond.label}</Link>
                  </p>
                )}
                <div className="tb-list-meta">
                  {shortWhen(n.createdAt)}
                  {n.readAt === null ? ' · unread' : ''}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
